// Toolbar pieces: quick filter, columns panel, filter panel, density and export menus.
import { useEffect, useId, useRef, useState } from "react";
import { Columns3, Download, Filter, Plus, Rows3, Search, Trash2, X } from "lucide-react";
import { cn } from "../../cn";
import { Button, IconButton } from "../button";
import { Checkbox, Input, NativeSelect } from "../field";
import { Menu, Popover } from "../overlay";
import type { GridLocaleText } from "./locale";
import { VALUELESS, colType, operatorsFor } from "./logic";
import type { GridColDef, GridColumnState, GridDensity, GridFilterItem, GridFilterModel } from "./types";

/** Value that settles `ms` after the last change (typing into server-backed filters). */
function useDebounced<T>(value: T, ms: number, onSettle: (v: T) => void) {
  // Latest callback in a ref: only value changes restart the timer.
  const cb = useRef(onSettle);
  cb.current = onSettle;
  useEffect(() => {
    const t = setTimeout(() => cb.current(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
}

export function QuickFilter({
  value,
  onChange,
  text,
}: {
  value: string;
  onChange: (v: string) => void;
  text: GridLocaleText;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  useDebounced(draft, 300, (v) => v !== value && onChange(v));
  return (
    <div role="search" className="relative flex w-full max-w-xs min-w-40 items-center">
      <Search aria-hidden className="pointer-events-none absolute left-3 size-4 text-ink-3" />
      <input
        type="search"
        aria-label={text.quickFilterPlaceholder}
        placeholder={text.quickFilterPlaceholder}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        className="h-9 w-full rounded-[var(--radius-control)] border border-line-strong bg-paper pr-8 pl-9 text-sm shadow-[var(--shadow-xs)] outline-none placeholder:text-ink-3 hover:border-ink-3 focus:border-primary focus:ring-4 focus:ring-primary/15 [&::-webkit-search-cancel-button]:hidden"
      />
      {draft && (
        <button
          type="button"
          aria-label={text.quickFilterClear}
          onClick={() => {
            setDraft("");
            onChange("");
          }}
          className="absolute right-1.5 inline-flex size-6 items-center justify-center rounded text-ink-3 hover:bg-hover hover:text-ink"
        >
          <X aria-hidden className="size-3.5" />
        </button>
      )}
    </div>
  );
}

export function ColumnsPanel<R>({
  columns,
  state,
  onToggle,
  onShowAll,
  onHideAll,
  onReset,
  text,
  open,
  onOpenChange,
}: {
  columns: GridColDef<R>[];
  state: GridColumnState;
  onToggle: (field: string, visible: boolean) => void;
  onShowAll: () => void;
  onHideAll: () => void;
  onReset: () => void;
  text: GridLocaleText;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const [q, setQ] = useState("");
  const byField = new Map(columns.map((c) => [c.field, c]));
  const list = state.order
    .map((f) => byField.get(f))
    .filter((c): c is GridColDef<R> => Boolean(c) && c!.hideable !== false)
    .filter((c) => c.headerName.toLowerCase().includes(q.toLowerCase()));
  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>
        <Button variant="ghost" data-testid="grid-columns-button">
          <Columns3 aria-hidden className="size-4" />
          {text.toolbarColumns}
        </Button>
      </Popover.Trigger>
      <Popover.Content label={text.toolbarColumns} className="w-72 p-0">
        <div className="border-b border-line p-3">
          <Input
            className="h-8"
            placeholder={text.columnsPanelSearch}
            aria-label={text.columnsPanelSearch}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="max-h-72 space-y-2 overflow-y-auto p-3">
          {list.map((c) => (
            <Checkbox
              key={c.field}
              label={c.headerName}
              checked={!state.hidden.includes(c.field)}
              onChange={(e) => onToggle(c.field, e.target.checked)}
            />
          ))}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-line bg-subtle px-3 py-2">
          <div className="flex gap-1">
            <Button variant="text" size="sm" onClick={onShowAll}>
              {text.columnsPanelShowAll}
            </Button>
            <Button variant="text" size="sm" onClick={onHideAll}>
              {text.columnsPanelHideAll}
            </Button>
          </div>
          <Button variant="ghost" size="sm" onClick={onReset}>
            {text.columnsPanelReset}
          </Button>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}

function FilterValueInput<R>({
  column,
  item,
  onChange,
  text,
}: {
  column: GridColDef<R>;
  item: GridFilterItem;
  onChange: (v: unknown) => void;
  text: GridLocaleText;
}) {
  const type = colType(column);
  const [draft, setDraft] = useState<unknown>(item.value ?? "");
  useEffect(() => setDraft(item.value ?? ""), [item.id, item.value]);
  useDebounced(draft, 300, (v) => v !== (item.value ?? "") && onChange(v));
  if (VALUELESS.has(item.operator)) return <div className="h-8" />;
  const label = text.filterPanelValue;
  if (type === "boolean")
    return (
      <NativeSelect
        aria-label={label}
        className="h-8"
        value={item.value === undefined ? "" : String(item.value)}
        onChange={(e) => onChange(e.target.value === "" ? undefined : e.target.value === "true")}
      >
        <option value="">{text.filterValueAny}</option>
        <option value="true">{text.filterValueTrue}</option>
        <option value="false">{text.filterValueFalse}</option>
      </NativeSelect>
    );
  if (type === "singleSelect" && item.operator === "isAnyOf") {
    const selected = Array.isArray(item.value) ? item.value.map(String) : [];
    return (
      <div
        role="group"
        aria-label={label}
        className="max-h-32 space-y-1.5 overflow-y-auto rounded-[var(--radius-control)] border border-line-strong p-2"
      >
        {(column.valueOptions ?? []).map((o) => (
          <Checkbox
            key={o.value}
            label={o.label}
            checked={selected.includes(o.value)}
            onChange={(e) =>
              onChange(e.target.checked ? [...selected, o.value] : selected.filter((v) => v !== o.value))
            }
          />
        ))}
      </div>
    );
  }
  if (type === "singleSelect")
    return (
      <NativeSelect
        aria-label={label}
        className="h-8"
        value={item.value === undefined ? "" : String(item.value)}
        onChange={(e) => onChange(e.target.value || undefined)}
      >
        <option value="">{text.filterValueAny}</option>
        {(column.valueOptions ?? []).map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </NativeSelect>
    );
  return (
    <Input
      aria-label={label}
      className="h-8"
      type={
        type === "number"
          ? "number"
          : type === "date"
            ? "date"
            : type === "dateTime"
              ? "datetime-local"
              : "text"
      }
      value={String(draft ?? "")}
      placeholder={text.filterPanelValue}
      onChange={(e) => setDraft(e.target.value)}
    />
  );
}

export function FilterPanel<R>({
  columns,
  model,
  onChange,
  text,
  open,
  onOpenChange,
  newItem,
  disableOr,
}: {
  columns: GridColDef<R>[];
  model: GridFilterModel;
  onChange: (m: GridFilterModel) => void;
  text: GridLocaleText;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  newItem: (field?: string) => GridFilterItem | null;
  disableOr?: boolean;
}) {
  const baseId = useId();
  const filterable = columns.filter((c) => c.filterable !== false && operatorsFor(c).length > 0);
  const active = model.items.filter(
    (i) => VALUELESS.has(i.operator) || (i.value !== undefined && i.value !== ""),
  ).length;
  const setItem = (id: string, patch: Partial<GridFilterItem>) =>
    onChange({ ...model, items: model.items.map((i) => (i.id === id ? { ...i, ...patch } : i)) });
  const remove = (id: string) => onChange({ ...model, items: model.items.filter((i) => i.id !== id) });
  if (!filterable.length) return null;
  return (
    <Popover.Root
      open={open}
      onOpenChange={(o) => {
        // Opening an empty panel starts with one row, like MUI.
        if (o && !model.items.length) {
          const first = newItem();
          if (first) onChange({ ...model, items: [first] });
        }
        onOpenChange(o);
      }}
    >
      <Popover.Trigger asChild>
        <Button variant="ghost" data-testid="grid-filters-button" aria-label={text.toolbarFilters}>
          <Filter aria-hidden className="size-4" />
          {text.toolbarFilters}
          {active > 0 && (
            <span
              aria-label={text.activeFilters(active)}
              className="inline-flex min-w-5 items-center justify-center rounded-full bg-primary-dark px-1.5 text-[11px] font-semibold text-white"
            >
              {active}
            </span>
          )}
        </Button>
      </Popover.Trigger>
      <Popover.Content label={text.toolbarFilters} className="w-[min(640px,calc(100vw-24px))]">
        {model.items.length === 0 && <p className="px-1 py-2 text-ink-2">{text.filterPanelEmpty}</p>}
        <div className="space-y-2">
          {model.items.map((item, idx) => {
            const column = filterable.find((c) => c.field === item.field) ?? filterable[0]!;
            const ops = operatorsFor(column);
            return (
              <div
                key={item.id}
                data-testid="grid-filter-row"
                className="grid grid-cols-[32px_72px_1fr] items-start gap-2 sm:grid-cols-[32px_72px_1fr_140px_1fr]"
              >
                <IconButton label={text.filterPanelDelete} className="size-8" onClick={() => remove(item.id)}>
                  <X aria-hidden className="size-4" />
                </IconButton>
                {idx === 0 ? (
                  <span />
                ) : idx === 1 && !disableOr ? (
                  <NativeSelect
                    aria-label={text.filterPanelLogic}
                    className="h-8 pr-7 pl-2"
                    value={model.logicOperator}
                    onChange={(e) => onChange({ ...model, logicOperator: e.target.value as "and" | "or" })}
                  >
                    <option value="and">{text.filterPanelAnd}</option>
                    <option value="or">{text.filterPanelOr}</option>
                  </NativeSelect>
                ) : (
                  <span className="flex h-8 items-center px-2 text-ink-2">
                    {model.logicOperator === "and" ? text.filterPanelAnd : text.filterPanelOr}
                  </span>
                )}
                <NativeSelect
                  aria-label={text.filterPanelColumn}
                  id={`${baseId}-col-${item.id}`}
                  className="h-8"
                  value={column.field}
                  onChange={(e) => {
                    const next = filterable.find((c) => c.field === e.target.value)!;
                    setItem(item.id, {
                      field: next.field,
                      operator: operatorsFor(next)[0]!,
                      value: undefined,
                    });
                  }}
                >
                  {filterable.map((c) => (
                    <option key={c.field} value={c.field}>
                      {c.headerName}
                    </option>
                  ))}
                </NativeSelect>
                <NativeSelect
                  aria-label={text.filterPanelOperator}
                  className="col-start-3 h-8 sm:col-start-auto"
                  value={ops.includes(item.operator) ? item.operator : ops[0]}
                  onChange={(e) => {
                    const multi = e.target.value === "isAnyOf" || item.operator === "isAnyOf";
                    setItem(item.id, { operator: e.target.value, ...(multi ? { value: undefined } : {}) });
                  }}
                >
                  {ops.map((o) => (
                    <option key={o} value={o}>
                      {text.operators[o] ?? o}
                    </option>
                  ))}
                </NativeSelect>
                <div className="col-start-3 sm:col-start-auto">
                  <FilterValueInput
                    column={column}
                    item={item}
                    text={text}
                    onChange={(v) => setItem(item.id, { value: v })}
                  />
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 flex items-center justify-between border-t border-line pt-3">
          <Button
            variant="text"
            size="sm"
            data-testid="grid-add-filter"
            onClick={() => {
              const next = newItem();
              if (next) onChange({ ...model, items: [...model.items, next] });
            }}
          >
            <Plus aria-hidden className="size-4" />
            {text.filterPanelAdd}
          </Button>
          {model.items.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => onChange({ ...model, items: [] })}>
              <Trash2 aria-hidden className="size-4" />
              {text.filterPanelRemoveAll}
            </Button>
          )}
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}

export function DensityMenu({
  value,
  onChange,
  text,
}: {
  value: GridDensity;
  onChange: (d: GridDensity) => void;
  text: GridLocaleText;
}) {
  return (
    <Menu.Root>
      <Menu.Trigger asChild>
        <Button variant="ghost">
          <Rows3 aria-hidden className="size-4" />
          {text.toolbarDensity}
        </Button>
      </Menu.Trigger>
      <Menu.Content align="start">
        {(["compact", "standard", "comfortable"] as const).map((d) => (
          <Menu.Item key={d} onSelect={() => onChange(d)}>
            <span className={cn("w-4 text-primary-dark", value !== d && "invisible")} aria-hidden>
              ✓
            </span>
            {text.density[d]}
          </Menu.Item>
        ))}
      </Menu.Content>
    </Menu.Root>
  );
}

export function ExportMenu({
  onExport,
  selected,
  text,
}: {
  onExport: (onlySelected: boolean) => void;
  selected: number;
  text: GridLocaleText;
}) {
  return (
    <Menu.Root>
      <Menu.Trigger asChild>
        <Button variant="ghost" data-testid="grid-export-button">
          <Download aria-hidden className="size-4" />
          {text.toolbarExport}
        </Button>
      </Menu.Trigger>
      <Menu.Content align="start">
        <Menu.Item onSelect={() => onExport(false)}>{text.exportCsv}</Menu.Item>
        {selected > 0 && (
          <Menu.Item onSelect={() => onExport(true)}>{text.exportSelected(selected)}</Menu.Item>
        )}
      </Menu.Content>
    </Menu.Root>
  );
}
